import React, { useEffect, useState } from "react";
import { FaVideo, FaRegFileAlt, FaTimes } from "react-icons/fa";
import DashboardHeader from "@/components/DashboardHeader";
import SearchInputComponent from "@/pages/eventListPage/SearchInputComponent";
import PaginationRow from "@/components/paginationRow/PaginationRow";
import Alert from "@/components/alert/Alert";
import ChangesAlert from "@/components/alert/ChangesAlert";
import { useAppSelector } from "@/hooks/hook";
import { GET, DELETE } from "@/services/api";
import { API_ROUTES } from "@/services/environment";
import { ApiResource, ResourceType } from "./interface";
import ResourceCard from "./components/ResourceCard";
import AddResourceModal from "./components/AddResourceModal";

const LIMIT = 8;

const tabIcon: Record<ResourceType, React.ReactNode> = {
    Video: <FaVideo />,
    Document: <FaRegFileAlt />,
};

const ResourcesPage = () => {
    const { roles } = useAppSelector((store) => store.user);
    const isAdmin = roles === "admin";
    const { listResourcesApi, addResourceApi } = API_ROUTES;

    const [activeFilter, setActiveFilter] = useState<ResourceType>("Video");
    const [searchInput, setSearchInput] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [activeVideo, setActiveVideo] = useState<string | null>(null);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [editingResource, setEditingResource] = useState<ApiResource | null>(null);
    const [deletingResource, setDeletingResource] = useState<ApiResource | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const [resources, setResources] = useState<ApiResource[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [pageNumber, setPageNumber] = useState(1);
    const [hasNext, setHasNext] = useState(false);
    const [totalResults, setTotalResults] = useState(0);
    const [tabCounts, setTabCounts] = useState<Record<ResourceType, number>>({ Video: 0, Document: 0 });
    const [refreshKey, setRefreshKey] = useState(0);

    const tabs: ResourceType[] = ["Video", "Document"];

    // Debounce search input before it drives the API call
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchInput.trim());
            setPageNumber(1);
        }, 500);
        return () => clearTimeout(handler);
    }, [searchInput]);

    // Reset to page 1 whenever the active tab changes
    useEffect(() => {
        setPageNumber(1);
    }, [activeFilter]);

    useEffect(() => {
        (async () => {
            try {
                setIsLoading(true);
                const query = new URLSearchParams({
                    type: activeFilter,
                    limit: String(LIMIT),
                    pageNumber: String(pageNumber),
                });
                if (debouncedSearch) query.set("search", debouncedSearch);
                if (!isAdmin) query.set("is_active", "true");

                const result = await GET(`${listResourcesApi}?${query.toString()}`);
                setResources(result?.data?.result ?? []);
                setHasNext(Boolean(result?.data?.hasNext));
                setTotalResults(result?.data?.totalResults ?? 0);
            } catch (error) {
                console.log("get resources error:", error);
            } finally {
                setIsLoading(false);
            }
        })();
    }, [listResourcesApi, activeFilter, pageNumber, debouncedSearch, refreshKey, isAdmin]);

    // Tab badge counts — independent of the active tab/pagination/search
    useEffect(() => {
        (async () => {
            try {
                const activeParam = isAdmin ? "" : "&is_active=true";
                const [videoResult, documentResult] = await Promise.all([
                    GET(`${listResourcesApi}?type=Video&limit=1&pageNumber=1${activeParam}`),
                    GET(`${listResourcesApi}?type=Document&limit=1&pageNumber=1${activeParam}`),
                ]);
                setTabCounts({
                    Video: videoResult?.data?.totalResults ?? 0,
                    Document: documentResult?.data?.totalResults ?? 0,
                });
            } catch (error) {
                console.log("get resource counts error:", error);
            }
        })();
    }, [listResourcesApi, refreshKey, isAdmin]);

    const currentStart = totalResults === 0 ? 0 : (pageNumber - 1) * LIMIT + 1;
    const currentEnd = Math.min(pageNumber * LIMIT, totalResults);

    const refreshList = () => {
        setPageNumber(1);
        setRefreshKey((key) => key + 1);
    };

    const confirmDelete = async () => {
        if (!deletingResource) return;
        setIsDeleting(true);
        try {
            const res = await DELETE(`${addResourceApi}/${deletingResource.id}`);
            Alert({ message: res?.data?.message ?? "Resource deleted" });
            setDeletingResource(null);
            refreshList();
        } catch (error: any) {
            Alert({ type: "error", message: error?.response?.data?.message ?? "Unable to delete resource" });
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <div>
            <div className="flex items-start md:items-center gap-1 flex-wrap justify-between mt-2 pb-1">
                <DashboardHeader width="w-full" title="Resources">
                    <div className="w-full md:w-auto mt-4 md:mt-0 flex flex-col sm:flex-row sm:justify-end gap-3">
                        <SearchInputComponent
                            handleSearch={(e) => setSearchInput(e.target.value)}
                            placeholder="Search resources..."
                        />
                        {isAdmin && (
                            <button
                                type="button"
                                onClick={() => {
                                    setEditingResource(null);
                                    setIsAddModalOpen(true);
                                }}
                                className="bg-black text-white rounded-full py-2.5 px-5 text-sm font-medium whitespace-nowrap self-end sm:self-auto"
                            >
                                + Add Resource
                            </button>
                        )}
                    </div>
                </DashboardHeader>
            </div>
            <p className="text-sm text-gray-500 max-w-2xl -mt-3 mb-5">
                Training videos and operational documents for tour leaders and agents.
                Everything your team needs before, during, and after a trip.
            </p>

            <div className="flex gap-2 flex-wrap mb-5">
                {tabs.map((tab) => (
                    <button
                        key={tab}
                        type="button"
                        onClick={() => setActiveFilter(tab)}
                        className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm border transition-colors ${activeFilter === tab
                            ? "bg-primary-red text-white border-primary-red"
                            : "border-gray-200 text-gray-600"
                            }`}
                    >
                        {tabIcon[tab]}
                        {tab}s <span className="opacity-70">{tabCounts[tab]}</span>
                    </button>
                ))}
            </div>

            {isLoading ? (
                <p className="text-center text-gray-500 text-sm py-8">Loading...</p>
            ) : resources.length > 0 ? (
                <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {resources.map((resource) => (
                            <ResourceCard
                                key={resource.id}
                                resource={resource}
                                onPlay={setActiveVideo}
                                isAdmin={isAdmin}
                                onEdit={(r) => {
                                    setEditingResource(r);
                                    setIsAddModalOpen(true);
                                }}
                                onDelete={setDeletingResource}
                            />
                        ))}
                    </div>
                    <PaginationRow
                        currentStart={currentStart}
                        currentEnd={currentEnd}
                        total={totalResults}
                        disablePrev={pageNumber <= 1}
                        disableNext={!hasNext}
                        onPrev={() => setPageNumber((p) => Math.max(1, p - 1))}
                        onNext={() => setPageNumber((p) => p + 1)}
                    />
                </>
            ) : (
                <p className="text-center text-gray-500 text-sm py-8">No resources found</p>
            )}

            {activeVideo && (
                <div
                    className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-6"
                    onClick={() => setActiveVideo(null)}
                >
                    <div
                        className="relative w-full max-w-3xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            type="button"
                            className="absolute -top-9 right-0 w-8 h-8 rounded-full bg-white/20 text-white flex items-center justify-center"
                            onClick={() => setActiveVideo(null)}
                        >
                            <FaTimes size={14} />
                        </button>
                        <div className="aspect-video rounded-xl overflow-hidden bg-black">
                            <iframe
                                className="w-full h-full"
                                src={`https://www.youtube.com/embed/${activeVideo}?autoplay=1&rel=0`}
                                title="Video player"
                                allow="autoplay; encrypted-media"
                                allowFullScreen
                            />
                        </div>
                    </div>
                </div>
            )}

            {isAdmin && (
                <AddResourceModal
                    isOpen={isAddModalOpen}
                    editingResource={editingResource}
                    onClose={() => {
                        setIsAddModalOpen(false);
                        setEditingResource(null);
                    }}
                    onSuccess={refreshList}
                />
            )}

            <ChangesAlert
                title="Delete Resource"
                desc={`Are you sure you want to delete "${deletingResource?.name}"? This cannot be undone.`}
                positiveButtonText="Delete"
                negativeButtonText="Cancel"
                isOpen={Boolean(deletingResource)}
                isLoading={isDeleting}
                handleSaveChanges={confirmDelete}
                handleDiscardChanges={() => setDeletingResource(null)}
            />
        </div>
    );
};

export default ResourcesPage;
